import * as React from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useTheme } from '@mui/material/styles'

import { isKeywordGraphApp } from '../../appVariant'
import ShareDialog from '../ShareDialog'

const codeSx = {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    px: 0.5,
    py: 0.1,
    borderRadius: '4px',
    backgroundColor: theme => theme.palette.action.hover,
}

// MCP server connection details, per app variant. Mirrors the config in
// home/QuickStartSection.jsx so the dialog matches the /apps page.
const mcpConfig = isKG => ({
    domain: isKG ? 'mcp.keywordgraph.com' : 'mcp.infranodus.com',
    pkg: isKG ? 'keywordgraph-mcp-server' : 'infranodus-mcp-server',
    serverKey: isKG ? 'keywordgraph' : 'infranodus',
    envVar: isKG ? 'KEYWORDGRAPH_API_KEY' : 'INFRANODUS_API_KEY',
})

function CopyButton({ text }) {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
            <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 },
                    p: 0.5,
                }}
            >
                {copied ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                )}
            </IconButton>
        </Tooltip>
    )
}

// Inline `code` chip followed by a small copy button, used in the intro
// paragraphs so each referenced value can be copied directly.
function InlineCode({ text }) {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
            <Box component="code" sx={codeSx}>
                {text}
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                        p: 0.2,
                        opacity: 0.6,
                        '&:hover': { opacity: 1 },
                    }}
                >
                    {copied ? (
                        <CheckIcon sx={{ fontSize: 13 }} />
                    ) : (
                        <ContentCopyIcon sx={{ fontSize: 13 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    )
}

// "Use in Your LLM via MCP server:" instruction, mirrored from the
// McpServerCard on the /apps page (home/QuickStartSection.jsx).
function McpServerCard() {
    const theme = useTheme()
    const { domain, pkg, serverKey, envVar } = mcpConfig(isKeywordGraphApp())
    const url = `https://${domain}`
    const claudeCodeCmd = `claude mcp add ${serverKey} -s user --transport http ${url}/mcp`
    const cursorConfig = `{
  "mcpServers": {
    "${serverKey}": {
      "url": "${url}"
    }
  }
}`
    const jsonConfig = `{
  "mcpServers": {
    "${serverKey}": {
      "command": "npx",
      "args": ["-y", "${pkg}"],
      "env": {
        "${envVar}": "your-api-key"
      }
    }
  }
}`
    const [mcpClient, setMcpClient] = React.useState('claude')
    const mcpClients = {
        claude: {
            label: 'Claude',
            code: url,
            caption: (
                <>
                    add this URL as a connector via{' '}
                    <strong>Settings &gt; Connectors</strong> in Claude Web /
                    Desktop and authenticate with your{' '}
                    <Link href="/api-access" underline="hover" target="_blank">
                        API key
                    </Link>
                    .
                </>
            ),
        },
        chatgpt: {
            label: 'ChatGPT',
            code: url,
            caption: (
                <>
                    open the ChatGPT{' '}
                    <Link
                        href="https://chatgpt.com/#settings/Connectors"
                        underline="hover"
                        target="_blank"
                    >
                        Apps
                    </Link>{' '}
                    page &gt; <strong>Advanced Settings</strong> &gt; enable{' '}
                    <strong>Developer Mode</strong> &gt;{' '}
                    <strong>Create App</strong>, paste this URL (leave the
                    OAuth fields empty), click Connect, and authenticate with
                    your{' '}
                    <Link href="/api-access" underline="hover" target="_blank">
                        API key
                    </Link>
                    .
                </>
            ),
        },
        'claude-code': {
            label: 'Claude Code',
            code: claudeCodeCmd,
            caption: (
                <>
                    then run <code>/mcp</code> in Claude Code and pick
                    &quot;Authenticate&quot; — the browser OAuth flow will ask
                    for your{' '}
                    <Link href="/api-access" underline="hover" target="_blank">
                        API key
                    </Link>
                    .
                </>
            ),
        },
        cursor: {
            label: 'Cursor',
            code: cursorConfig,
            caption: (
                <>
                    add this to <code>~/.cursor/mcp.json</code> (or{' '}
                    <strong>Cursor Settings &gt; MCP</strong>), then click
                    &quot;Login&quot; next to the server to authenticate with
                    your{' '}
                    <Link href="/api-access" underline="hover" target="_blank">
                        API key
                    </Link>
                    .
                </>
            ),
        },
        other: {
            label: 'Other Clients',
            code: jsonConfig,
            caption: (
                <>
                    add this to your client&apos;s{' '}
                    <a href="/mcp" target="_blank" rel="noopener noreferrer">
                        MCP config
                    </a>{' '}
                    with your{' '}
                    <Link href="/api-access" underline="hover" target="_blank">
                        API key
                    </Link>
                    .
                </>
            ),
        },
    }
    const selected = mcpClients[mcpClient]

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: '10px',
                border: `1px solid ${theme.palette.divider}`,
                bgcolor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(0,0,0,0.02)',
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Use in Your LLM via MCP server:
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 0.3 }}>
                1.{' '}
                <Link href="/api-access" underline="hover" target="_blank">
                    Get your API key
                </Link>
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                2. Connect the MCP server in your client:
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mb: 0.75,
                }}
            >
                {Object.entries(mcpClients).map(([key, option]) => (
                    <Chip
                        key={key}
                        label={option.label}
                        size="small"
                        clickable
                        color={mcpClient === key ? 'primary' : 'default'}
                        variant={mcpClient === key ? 'filled' : 'outlined'}
                        onClick={() => setMcpClient(key)}
                    />
                ))}
            </Box>
            <Box sx={{ position: 'relative', mb: 0.5 }}>
                <Box
                    component={selected.code.includes('\n') ? 'pre' : 'code'}
                    sx={{
                        display: 'block',
                        fontSize: selected.code.includes('\n')
                            ? '9.5px'
                            : '11px',
                        bgcolor: theme.palette.action.hover,
                        borderRadius: '4px',
                        px: 1,
                        py: 0.5,
                        pr: 4,
                        m: 0,
                        overflow: 'auto',
                        whiteSpace: selected.code.includes('\n')
                            ? 'pre'
                            : 'normal',
                        wordBreak: 'break-all',
                        lineHeight: 1.4,
                    }}
                >
                    {selected.code}
                </Box>
                <CopyButton text={selected.code} />
            </Box>
            <Typography
                variant="caption"
                component="div"
                sx={{
                    mb: 0.3,
                    px: 1.25,
                    py: 0.75,
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    bgcolor: theme.palette.action.hover,
                    borderRadius: '0 6px 6px 0',
                }}
            >
                {selected.caption}
            </Typography>
        </Box>
    )
}

/**
 * "Use this Graph as a Brain in Your LLM" dialog.
 *
 * Explains how to surface the current graph's content and reasoning rules
 * to an LLM through the InfraNodus MCP server, then reproduces the
 * "Use in Your LLM via MCP server" setup card shown on the /apps page.
 */
export default function BrainDialog({ open, onClose, graphName }) {
    const [shareOpen, setShareOpen] = React.useState(false)

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pr: 6 }}>
                    Use this Graph as a Brain in Your LLM
                    <IconButton
                        aria-label="close-brain-dialog"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme => theme.palette.text.secondary,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        You can make this graph's content and reasoning rules
                        available in your favorite LLM using our MCP server.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Just refer to the graph's name{' '}
                        <InlineCode text={graphName} /> or use the{' '}
                        <InlineCode text="retrieve_from_knowledge_base" /> tool
                        to get graph context for a prompt or the{' '}
                        <InlineCode text="generate_responses_from_graph" /> to
                        generate a response from the graph.
                    </Typography>
                    <Typography variant="body2">
                        You can also make this graph available to anybody else
                        using the MCP server if you{' '}
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => setShareOpen(true)}
                            sx={{ verticalAlign: 'baseline' }}
                        >
                            make it public
                        </Link>
                        .
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <McpServerCard />
                </DialogContent>
            </Dialog>
            <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
        </>
    )
}
